/**
 * Preview Config Context
 * 
 * Allows overriding store config values for preview purposes.
 * Wrap preview components with PreviewConfigProvider to show unsaved changes.
 */

import React, { createContext, useContext, useMemo } from 'react';
import { useStoreConfig, NormalizedStoreConfig } from './StoreConfigContext';

interface PreviewConfigOverrides {
    primaryFont?: string | null;
    secondaryFont?: string | null;
    logoFont?: string | null;
    primaryColor?: string;
    // Add more overridable fields as needed
}

interface PreviewConfigContextType {
    config: NormalizedStoreConfig;
    isPreview: boolean;
}

const PreviewConfigContext = createContext<PreviewConfigContextType | null>(null);

interface PreviewConfigProviderProps {
    children: React.ReactNode;
    overrides: PreviewConfigOverrides;
}

/**
 * Provider that wraps preview components to override specific config values
 */
export const PreviewConfigProvider: React.FC<PreviewConfigProviderProps> = ({ 
    children, 
    overrides 
}) => {
    const { config: baseConfig } = useStoreConfig();

    const previewConfig = useMemo(() => ({
        ...baseConfig,
        ...overrides,
    }), [baseConfig, overrides]);

    const value = useMemo(() => ({
        config: previewConfig,
        isPreview: true,
    }), [previewConfig]);

    return (
        <PreviewConfigContext.Provider value={value}>
            {children}
        </PreviewConfigContext.Provider>
    );
};

/**
 * Hook to get config - returns preview config if inside PreviewConfigProvider,
 * otherwise returns the regular store config
 */
export const usePreviewableConfig = () => {
    const previewContext = useContext(PreviewConfigContext);
    const storeConfig = useStoreConfig();

    if (previewContext) {
        return {
            config: previewContext.config,
            isPreview: previewContext.isPreview,
        };
    }

    return {
        config: storeConfig.config,
        isPreview: false,
    };
};
